import { useState, useEffect, useRef, type CSSProperties, type FormEvent } from 'react'
import { X, MessageCircle, Calendar, ArrowRight, ExternalLink, Phone, Send, Sparkles, Bot, Headphones } from 'lucide-react'

/* ------------------------------------------------------------------
   Chef Buddy Motion Graphical Interactive Mascot
   Single happy, smiling 3D video loop with cursor-responsive
   3D perspective tilt, spring-boop haptics, and radiant ambient halo.
   (No crying / weeping frames)
------------------------------------------------------------------ */

interface ChefBuddyVideoAvatarProps {
  size?: number
  isAwake?: boolean
  onClick?: () => void
  onAwakeChange?: (awake: boolean) => void
  className?: string
}

export function ChefBuddyVideoAvatar({
  size = 120,
  isAwake: forceActive = false,
  onClick,
  onAwakeChange,
  className = '',
}: ChefBuddyVideoAvatarProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isNear, setIsNear] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isBooping, setIsBooping] = useState(false)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })

  const active = forceActive || isHovered || isNear

  useEffect(() => {
    onAwakeChange?.(active)
  }, [active, onAwakeChange])

  // Mouse tracking: smooth 3D tilt + proximity zone
  useEffect(() => {
    const PROXIMITY_RADIUS = 260

    const handlePointerMove = (e: MouseEvent | PointerEvent) => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const dx = e.clientX - centerX
      const dy = e.clientY - centerY
      const dist = Math.hypot(dx, dy)

      setIsNear(dist < PROXIMITY_RADIUS)

      // Interactive 3D tilt tracking (max 10 degrees)
      if (dist < 500) {
        const tiltX = Math.max(-10, Math.min(10, (dy / 500) * -10))
        const tiltY = Math.max(-10, Math.min(10, (dx / 500) * 10))
        setTilt({ x: tiltX, y: tiltY })
      } else {
        setTilt({ x: 0, y: 0 })
      }
    }

    const handleMouseLeave = () => {
      setIsNear(false)
      setTilt({ x: 0, y: 0 })
    }

    window.addEventListener('mousemove', handlePointerMove, { passive: true })
    window.addEventListener('pointermove', handlePointerMove, { passive: true })
    window.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      window.removeEventListener('mousemove', handlePointerMove)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  // Ensure happy video stream loops smoothly
  useEffect(() => {
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(() => {})
    }
  }, [])

  const handleClick = () => {
    setIsBooping(true)
    setTimeout(() => setIsBooping(false), 220)
    onClick?.()
  }

  return (
    <div
      id="chef-buddy-avatar-btn"
      ref={containerRef}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      aria-label="Chef Buddy Interactive Dining Concierge - Click to chat"
      className={`relative cursor-pointer select-none group ${className}`}
      style={{
        width: size,
        height: size,
        perspective: 600,
      }}
    >
      {/* 3D Motion Graphical Interactive Layer */}
      <div
        className="relative w-full h-full"
        style={{
          transform: `perspective(600px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${
            isBooping ? 0.92 : isHovered ? 1.08 : isNear ? 1.04 : 1
          })`,
          transition: isBooping
            ? 'transform 0.12s ease-out'
            : 'transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Soft Ambient Radial Halo Glow */}
        <div
          className={`absolute -inset-2 rounded-full blur-xl transition-all duration-500 pointer-events-none ${
            active
              ? 'bg-gradient-to-tr from-amber-500/45 via-amber-400/30 to-orange-500/35 scale-110 opacity-100'
              : 'bg-amber-500/20 scale-95 opacity-60'
          }`}
        />

        {/* Circular Glass Bezel with Amber Rim */}
        <div
          className={`relative w-full h-full rounded-full overflow-hidden border-2 shadow-2xl transition-all duration-300 bg-[#727775] ${
            active
              ? 'border-amber-400 shadow-amber-500/30 ring-4 ring-amber-400/25'
              : 'border-stone-700/80 shadow-black/60 ring-2 ring-stone-800/40 hover:border-amber-400/70'
          }`}
        >
          {/* Happy, Smiling Mascot Video (Mascot - New.mp4) - Zero crying! */}
          <video
            ref={videoRef}
            src="/mascots/buddy-awake.mp4"
            poster="/mascots/buddy-awake-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Status Badge - Online & Ready (No Zzz) */}
        <div
          className={`absolute -bottom-1 right-1.5 flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[10px] font-bold shadow-lg backdrop-blur-md transition-all duration-300 pointer-events-none ${
            active
              ? 'bg-[#18181b]/95 border-emerald-400/60 text-emerald-300 scale-100 shadow-emerald-500/20'
              : 'bg-[#18181b]/85 border-stone-700 text-stone-300 scale-95'
          }`}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span>Online</span>
        </div>
      </div>
    </div>
  )
}

export const ChefBuddyInteractiveAvatar = ChefBuddyVideoAvatar;

/* ------------------------------------------------------------------
   Chef Buddy Mascot + Interactive Native Chatbot Component
------------------------------------------------------------------ */

interface MessageAction {
  label: string
  actionType?: 'demo' | 'trial' | 'whatsapp' | 'tawk' | 'pricing' | 'sommelier'
  href?: string
  primary?: boolean
}

interface ChatMessage {
  id: string
  sender: 'buddy' | 'user'
  text: string
  time: string
  actions?: MessageAction[]
}

const QUICK_PROMPTS = [
  'I have a question',
  'Tell me more',
  '🍷 AI Sommelier',
  '💳 Pricing & trial',
  '📅 Book a demo',
  '👨‍💼 Talk to live human',
]

function getBuddyResponse(queryText: string): { text: string; actions?: MessageAction[] } {
  const q = queryText.toLowerCase().trim()

  if (q.includes('sommelier') || q.includes('wine') || q.includes('cocktail') || q.includes('pair')) {
    return {
      text: "🍷 Our signature AI Sommelier acts as a dedicated Master Sommelier at every table! Guests scan your luxury QR menu and ask natural pairing questions (*'What pairs best with our Wagyu Ribeye?'* or *'Recommend an organic Italian red'*). It analyzes flavor profiles, notes, and allergens in 18+ languages, driving a proven +25% increase in beverage spend.",
      actions: [
        { label: 'Book a 1-on-1 Demo', actionType: 'demo', primary: true },
        { label: 'Start 15-Day Free Trial', href: 'https://www.thatkorner.online/restaurant-signup' },
        { label: 'Chat on WhatsApp', actionType: 'whatsapp' },
      ],
    }
  }

  if (q.includes('price') || q.includes('pricing') || q.includes('cost') || q.includes('plan') || q.includes('trial')) {
    return {
      text: "💳 We offer transparent, high-ROI pricing for boutique bistros and Michelin-level venues alike. Start with our 15-Day Free Trial—zero credit card required, instant onboarding, and complete menu digitization within 48 hours.",
      actions: [
        { label: 'Start 15-Day Trial (No Card)', href: 'https://www.thatkorner.online/restaurant-signup', primary: true },
        { label: 'Schedule 15-Min Walkthrough', actionType: 'demo' },
        { label: 'Discuss Custom Pricing', actionType: 'whatsapp' },
      ],
    }
  }

  if (q.includes('demo') || q.includes('book') || q.includes('meeting') || q.includes('walkthrough')) {
    return {
      text: "📅 We'd be thrilled to give you a private 15-minute walkthrough showcasing thatKorner loaded with your own restaurant's branding, cuisine, and wine list. Would you like to schedule it on our page or message our founder directly on WhatsApp?",
      actions: [
        { label: 'Fill Demo Request Form', actionType: 'demo', primary: true },
        { label: 'Instant WhatsApp (+971 55 976 7778)', actionType: 'whatsapp' },
        { label: 'Direct Hotline Call', href: 'tel:+971559767778' },
      ],
    }
  }

  if (q.includes('human') || q.includes('agent') || q.includes('support') || q.includes('person') || q.includes('talk') || q.includes('live')) {
    return {
      text: "👨‍💼 Our live hospitality team is online and ready to assist you right now! Choose your preferred channel below to speak with an agent:",
      actions: [
        { label: 'Chat on WhatsApp (+971 55 976 7778)', actionType: 'whatsapp', primary: true },
        { label: 'Open Tawk.to Live Desk', actionType: 'tawk' },
        { label: 'Direct Phone Call', href: 'tel:+971559767778' },
      ],
    }
  }

  if (q.includes('tell me more') || q.includes('what is') || q.includes('about') || q.includes('feature')) {
    return {
      text: "✨ thatKorner is the luxury digital dining operating system. We replace paper menus with cinema-grade interactive digital menus featuring our signature AI Sommelier, automated dietary & allergen filters, and seamless POS/WhatsApp ordering. Your menu can be live in 48 hours!",
      actions: [
        { label: 'Explore AI Sommelier', actionType: 'sommelier', primary: true },
        { label: 'Book Private Demo', actionType: 'demo' },
        { label: 'Start 15-Day Free Trial', href: 'https://www.thatkorner.online/restaurant-signup' },
      ],
    }
  }

  // Default helpful response
  return {
    text: "Thanks for asking! I'm here to help you explore thatKorner's digital dining experience, calculate revenue uplift, or connect you directly with our hospitality specialists.",
    actions: [
      { label: 'Book a 15-min Demo', actionType: 'demo', primary: true },
      { label: 'What is AI Sommelier?', actionType: 'sommelier' },
      { label: 'Message on WhatsApp', actionType: 'whatsapp' },
      { label: 'Speak to Live Agent', actionType: 'tawk' },
    ],
  }
}

export function ChefBuddyMascot() {
  const [isOpen, setIsOpen] = useState(false)
  const [isBuddyAwake, setIsBuddyAwake] = useState(false)
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'init-1',
      sender: 'buddy',
      text: "👋 Hi! How can we help? I'm Chef Buddy, your 24/7 AI culinary concierge at thatKorner.",
      time: 'Just now',
    },
    {
      id: 'init-2',
      sender: 'buddy',
      text: "I can answer questions about our digital luxury QR menus, demo our AI Sommelier, or help you book a private walkthrough. What would you like to explore?",
      time: 'Just now',
      actions: [
        { label: 'I have a question', actionType: 'sommelier' },
        { label: 'Tell me more', actionType: 'sommelier' },
        { label: 'Book a Demo', actionType: 'demo', primary: true },
        { label: 'Talk to Human Agent', actionType: 'tawk' },
      ],
    },
  ])

  const chatBodyRef = useRef<HTMLDivElement>(null)

  // Initialize Tawk.to silently in background
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (document.getElementById('tawk-embed-script')) return

    const w = window as any
    w.Tawk_API = w.Tawk_API || {}
    w.Tawk_LoadStart = new Date()

    w.Tawk_API.onLoad = function () {
      try {
        if (typeof w.Tawk_API.hideWidget === 'function') {
          w.Tawk_API.hideWidget()
        }
      } catch (err) {
        console.warn('Tawk hideWidget error:', err)
      }
    }

    w.Tawk_API.onChatMinimized = function () {
      try {
        if (typeof w.Tawk_API.hideWidget === 'function') {
          w.Tawk_API.hideWidget()
        }
      } catch (err) {
        console.warn('Tawk hideWidget onChatMinimized error:', err)
      }
    }

    const s = document.createElement('script')
    s.id = 'tawk-embed-script'
    s.async = true
    s.src = 'https://embed.tawk.to/6ac60022f7849b34c2f7cc67/1k4an12g2'
    s.charset = 'UTF-8'
    s.setAttribute('crossorigin', '*')
    document.body.appendChild(s)
  }, [])

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
    }
  }, [messages, isTyping, isOpen])

  const openTawkChat = () => {
    const w = window as any
    if (w.Tawk_API) {
      try {
        if (typeof w.Tawk_API.showWidget === 'function') {
          w.Tawk_API.showWidget()
        }
        if (typeof w.Tawk_API.maximize === 'function') {
          w.Tawk_API.maximize()
        } else if (typeof w.Tawk_API.toggle === 'function') {
          w.Tawk_API.toggle()
        }
      } catch (err) {
        console.warn('Failed to open Tawk chat:', err)
      }
    }
  }

  const handleBookDemo = () => {
    setIsOpen(false)
    const contactElem = document.getElementById('contact')
    if (contactElem) {
      contactElem.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.location.hash = '#contact'
    }
  }

  const handleActionClick = (action: MessageAction) => {
    if (action.href) {
      window.open(action.href, '_blank', 'noopener,noreferrer')
      return
    }

    if (action.actionType === 'demo') {
      handleBookDemo()
      return
    }

    if (action.actionType === 'tawk') {
      openTawkChat()
      return
    }

    if (action.actionType === 'whatsapp') {
      window.open(
        'https://wa.me/971559767778?text=Hello%20thatKorner%20team,%20I%20would%20like%20to%20learn%20more%20about%20your%20digital%20menu%20and%20AI%20Sommelier.',
        '_blank',
        'noopener,noreferrer'
      )
      return
    }

    handleSendMessage(action.label)
  }

  const handleSendMessage = (textToSend?: string) => {
    const text = (textToSend ?? inputText).trim()
    if (!text) return

    if (!textToSend) {
      setInputText('')
    }

    const now = new Date()
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text,
      time: timeStr,
    }

    setMessages((prev) => [...prev, userMsg])
    setIsTyping(true)

    // Respond after brief realistic typing delay
    setTimeout(() => {
      const response = getBuddyResponse(text)
      const botMsg: ChatMessage = {
        id: `b-${Date.now()}`,
        sender: 'buddy',
        text: response.text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        actions: response.actions,
      }
      setIsTyping(false)
      setMessages((prev) => [...prev, botMsg])
    }, 550)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    handleSendMessage()
  }

  return (
    <div
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end pointer-events-none select-none font-sans"
      aria-label="Chef Buddy Interactive Mascot"
    >
      {/* Integrated Mascot Chat Window */}
      {isOpen && (
        <div className="pointer-events-auto mb-3 w-[360px] sm:w-[390px] max-w-[calc(100vw-28px)] h-[520px] max-h-[calc(100vh-140px)] flex flex-col rounded-3xl border border-amber-500/25 bg-[#121212]/95 shadow-2xl shadow-black/80 backdrop-blur-2xl text-stone-100 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-stone-800/80 bg-gradient-to-r from-stone-900/90 via-[#18181b] to-stone-900/90 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src="/mascots/buddy-awake-poster.jpg"
                  alt="Chef Buddy"
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-amber-500/40 bg-gradient-to-b from-amber-500/20 to-transparent p-0.5"
                />
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#121212]" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-stone-100">Chef Buddy</span>
                  <span className="rounded-full bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                    AI Concierge
                  </span>
                </div>
                <p className="text-[11px] text-emerald-400 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · thatKorner Assistant
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <a
                href="https://wa.me/971559767778"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full p-2 text-stone-400 hover:text-emerald-400 hover:bg-stone-800/60 transition-colors"
                title="Chat directly on WhatsApp (+971 55 976 7778)"
              >
                <Phone className="h-4 w-4" />
              </a>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-full p-2 text-stone-400 hover:text-white hover:bg-stone-800/60 transition-colors"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages Scroll Area */}
          <div
            ref={chatBodyRef}
            className="flex-1 overflow-y-auto p-4 space-y-3.5 select-text"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#262626 transparent' }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-2.5 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.sender === 'buddy' && (
                  <img
                    src="/mascots/buddy-awake-poster.jpg"
                    alt="Chef Buddy"
                    className="h-7 w-7 rounded-full object-cover ring-1 ring-amber-500/30 bg-amber-500/10 flex-shrink-0 mt-0.5"
                  />
                )}
                <div
                  className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-xs leading-relaxed ${
                    msg.sender === 'user'
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white font-medium rounded-br-xs'
                      : 'bg-stone-900/90 border border-stone-800 text-stone-200 rounded-bl-xs'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{msg.text}</p>

                  {/* Contextual Action Buttons */}
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5 pt-1">
                      {msg.actions.map((act, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleActionClick(act)}
                          className={`rounded-xl px-2.5 py-1 text-[11px] font-semibold transition-all ${
                            act.primary
                              ? 'bg-amber-500 text-stone-950 hover:bg-amber-400 shadow-sm'
                              : 'border border-amber-500/40 bg-stone-950/60 text-amber-300 hover:bg-stone-800 hover:border-amber-400'
                          }`}
                        >
                          {act.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <span className="mt-1 block text-[9px] text-stone-400 opacity-60 text-right">
                    {msg.time}
                  </span>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex gap-2.5 items-center">
                <img
                  src="/mascots/buddy-awake-poster.jpg"
                  alt="Chef Buddy"
                  className="h-7 w-7 rounded-full object-cover ring-1 ring-amber-500/30 bg-amber-500/10 flex-shrink-0"
                />
                <div className="flex items-center gap-1 rounded-2xl bg-stone-900/90 border border-stone-800 px-3 py-2 text-stone-400 text-xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  <span className="ml-1 text-[11px] text-stone-400">Chef Buddy is typing...</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick Reply Pills */}
          <div className="px-3 py-2 border-t border-stone-800/60 bg-[#141416]/70 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {QUICK_PROMPTS.map((prompt, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSendMessage(prompt)}
                className="flex-shrink-0 rounded-full border border-stone-800 bg-stone-900/80 px-3 py-1 text-[11px] text-stone-300 transition-all hover:border-amber-500/50 hover:bg-stone-800 hover:text-amber-200 active:scale-95"
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Chat Input Bar */}
          <form
            onSubmit={handleSubmit}
            className="p-3 border-t border-stone-800/80 bg-stone-950/80 flex items-center gap-2"
          >
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Ask Chef Buddy anything..."
              className="flex-1 rounded-xl bg-stone-900/90 border border-stone-800 px-3.5 py-2.5 text-xs text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40"
            />
            <button
              type="submit"
              disabled={!inputText.trim()}
              className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 p-2.5 text-stone-950 font-semibold shadow-md disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-95 transition-all"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}

      {/* Mascot Avatar Launcher */}
      <div className="pointer-events-auto flex items-end gap-2.5">
        {!isOpen && (
          <button
            id="chef-buddy-launcher-pill"
            onClick={() => setIsOpen(true)}
            className={`mb-3 flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold shadow-xl backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95 ${
              isBuddyAwake
                ? 'border-amber-400 bg-amber-500/10 text-amber-200 ring-2 ring-amber-400/20'
                : 'border-amber-500/35 bg-[#121212]/95 text-amber-300 hover:border-amber-400 hover:text-amber-200'
            }`}
            title="Chat with Chef Buddy!"
          >
            {isBuddyAwake ? (
              <Sparkles className="h-3.5 w-3.5 text-amber-400 animate-spin" style={{ animationDuration: '3s' }} />
            ) : (
              <MessageCircle className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            )}
            <span>{isBuddyAwake ? 'Hey! Ask me anything 👋' : 'Chat with Chef Buddy'}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </button>
        )}

        <ChefBuddyVideoAvatar
          size={120}
          isAwake={isOpen}
          onAwakeChange={setIsBuddyAwake}
          onClick={() => setIsOpen((prev) => !prev)}
        />
      </div>
    </div>
  )
}
